import React, { useState } from 'react';
import { AccordionBody, AccordionHeader, AccordionItem, Accordion as RSAccordion } from 'reactstrap';
import classNames from 'classnames';

interface Item {
  header: string | React.ReactNode;
  bodyComponent: React.ComponentType<any>;
  bodyProps?: Record<string, any>;
  isLoading?: boolean;
}

interface ComponentProps {
  className?: string;
  items: Item[];
  allowMultiple?: boolean;
  stickyHeaders?: boolean;
}

const AccordionComponent: React.FC<ComponentProps> = ({
  className,
  items,
  allowMultiple = false,
  stickyHeaders = false,
}) => {
  const [open, setOpen] = useState<string | string[]>('');

  const toggle = (target: string) => {
    if (!allowMultiple) return setOpen(target !== open ? target : '');
    toggleMultiple(target);
  };

  const toggleMultiple = (target: string) => {
    if (!Array.isArray(open)) return setOpen([target]);

    const exists = open.find((id) => target === id);
    if (exists) return setOpen(open.filter((id) => target !== id));
    return setOpen([...open, target]);
  };

  const isOpen = (id: string) => {
    if (allowMultiple) return open?.includes(id);
    return open === id;
  };

  const renderItems = (items: Item[]) =>
    items.map(({ header, bodyComponent: BodyComponent, bodyProps = {}, isLoading = false }, index) => {
      const id = String(index);
      const itemClassName = classNames({
        expanded: isOpen(id),
      });

      const headerClassName = classNames({
        'sticky-header': stickyHeaders,
      });

      const isItemOpen = isOpen(id);

      return (
        <AccordionItem key={id} className={itemClassName}>
          <AccordionHeader targetId={id} className={headerClassName}>
            {header}
            {isLoading && (
              <span className="loader spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true"></span>
            )}
          </AccordionHeader>
          <AccordionBody accordionId={id}>
            <BodyComponent {...bodyProps} isOpen={isItemOpen} />
          </AccordionBody>
        </AccordionItem>
      );
    });

  const containerClassName = classNames(className, { 'accordion-with-sticky-headers': stickyHeaders });

  return (
    <RSAccordion open={open} toggle={toggle} className={containerClassName}>
      {renderItems(items)}
    </RSAccordion>
  );
};

const Accordion = React.memo(AccordionComponent);
Accordion.displayName = 'Accordion';

export default Accordion;
